import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import withImmutablePropsToJS from "with-immutable-props-to-js"
import debounce from "lodash.debounce"

import InfiniteScroll from "react-infinite-scroller"

import { widgetCreateFormConnection } from "app/static/frontend/dashboards/selectors"

// action creators
import { segueToUrl } from "app/static/frontend/global-action-creators"
import {
    clearWidgetTemplateList,
    getWidgetTemplates,
    submitWidgetTemplateSelection,
} from "app/static/frontend/dashboards/action-creators/widgetCreateFormActionCreators"

import { dashboardPaths } from "app/static/frontend/imports/desktopPaths"

// components
import {
    Button,
    FilterButton,
    ModalBody,
    ModalContainer,
    ModalFooter,
    ModalHeader,
    SearchBar,
    TemplateOptions,
} from "quorumdesign"

import LoadingSpinner from "app/static/frontend/dashboards/components/LoadingSpinner"

const widgetFormTabs = {
    newWidget: {
        value: 0,
        tabTitle: "New",
        selectButtonText: "Continue",
        defaultSelectedValue: DjangIO.app.widgets.types.DashboardWidgetType.list_widget.value,
    },
    templates: {
        value: 1,
        tabTitle: "Templates",
        selectButtonText: "Submit",
        defaultSelectedValue: undefined,
    },
}

export const WidgetCreateForm = ({ closeModal, ...props }) => {
    const [filter, updateFilter] = useState("")
    const [selected, updateSelected] = useState(DjangIO.app.widgets.types.DashboardWidgetType.list_widget.value)
    const [tabIndex, updateTabIndex] = useState(0)

    const currentTab = Object.values(widgetFormTabs).find((tab) => tab.value === tabIndex)

    // load the first page of widget templates when the component mounts
    useEffect(() => {
        props.getWidgetTemplates({ offset: 0 })
    }, [])

    const newWidgetTypes = DjangIO.app.widgets.types.DashboardWidgetType.region_supported_items()
        .map((each) => ({
            ...each,
            title: each.label,
            text: each.tip,
            selected: each.value === selected,
            iconSize: "2x",
        }))
        .sort(DjangIO.sortByKey("sort"))
        .filter((e) => e.title.toLowerCase().includes(filter.toLowerCase()))

    const premadeTemplates = props.widgetTemplates
        ? props.widgetTemplates.map((template) => ({
              ...template,
              value: template.id,
              selected: selected === template.id,
          }))
        : []

    const templateCount =
        tabIndex === widgetFormTabs.newWidget.value
            ? DjangIO.app.widgets.types.DashboardWidgetType.region_supported_items().length
            : premadeTemplates.length

    const selectButtonCallback =
        tabIndex === widgetFormTabs.newWidget.value
            ? () =>
                  props.segueToUrl(
                      dashboardPaths.createWidget
                          .replace(":dashboardId", props.dashboardId)
                          .replace(":widgetType", selected),
                  )
            : () =>
                  props.submitWidgetTemplateSelection({
                      dashboardId: props.dashboardId,
                      // TODO this will crash if the selection is bad / not present in loaded templates
                      widget: premadeTemplates.find((template) => template.id === selected)["widget"].toJS(),
                  })

    return (
        <ModalContainer onClose={closeModal}>
            <ModalHeader onClose={closeModal} title={"Choose a New Widget"} />
            <ModalBody
                text={"Choose a widget to keep on top of day to day work and see a bigger picture view of your impact"}
                rightSideComponent={[
                    <SearchBar
                        placeholder="Search..."
                        onChange={
                            tabIndex === widgetFormTabs.templates.value
                                ? debounce((e) => {
                                      // empty the stored templates since they don't match the search terms
                                      props.clearWidgetTemplateList()

                                      // load new templates matching the search term
                                      props.getWidgetTemplates({
                                          offset: 0,
                                          filters: { quick_search: e },
                                      })

                                      updateFilter(e)

                                      // reset the selection so we can't submit a template from before
                                      // the new results have loaded
                                      updateSelected(currentTab.defaultSelectedValue)
                                  }, 750)
                                : (e) => updateFilter(e)
                        }
                        fullWidth={true}
                        key="searchBar"
                    />,
                    <FilterButton
                        selectedIndex={tabIndex}
                        menuOptions={Object.values(widgetFormTabs).map((tab) => ({
                            text: tab.tabTitle,
                            onClick: () => {
                                updateTabIndex(tab.value)
                                updateSelected(tab.defaultSelectedValue)
                            },
                        }))}
                        key="tabFilterButton"
                    />,
                ]}
            >
                <InfiniteScroll
                    data-cy="list-view-results"
                    loadMore={debounce(
                        () =>
                            props.getWidgetTemplates({
                                offset: templateCount,
                                filters: { quick_search: filter },
                            }),
                        1000,
                    )}
                    hasMore={
                        // Do not fetch data while scrolling the new widget tab
                        tabIndex === widgetFormTabs.templates.value &&
                        // Do not fetch data if there is no more
                        props.hasMoreTemplates
                    }
                    threshold={150}
                    initialLoad={false}
                    useWindow={false}
                    className="list-view list-view-results"
                    loader={<LoadingSpinner />}
                >
                    <TemplateOptions
                        templates={tabIndex === widgetFormTabs.newWidget.value ? newWidgetTypes : premadeTemplates}
                        selectTemplate={({ value }) => updateSelected(value)}
                        cardSize="medium"
                        templateCount={templateCount}
                    />
                </InfiniteScroll>
            </ModalBody>
            <ModalFooter
                leftSide={<Button isGiant text="Cancel" type="secondary" onClick={closeModal} />}
                rightSide={
                    <Button
                        isGiant
                        dataCy="submit-widget"
                        disabled={!selected}
                        text={currentTab.selectButtonText}
                        onClick={selectButtonCallback}
                    />
                }
            />
        </ModalContainer>
    )
}

export default connect(widgetCreateFormConnection, {
    clearWidgetTemplateList,
    getWidgetTemplates,
    segueToUrl,
    submitWidgetTemplateSelection,
})(withImmutablePropsToJS(WidgetCreateForm))
